import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Flower2, ShoppingBag, MessageCircle, Sparkles, Leaf, ArrowRight, FileUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContent } from "@/lib/content";
import FeaturedProducts from "@/components/home/FeaturedProducts";
import ProgramCards from "@/components/home/ProgramCards";
import EntryPopup from "@/components/EntryPopup";

// Copy and imagery come from Admin -> Site Content; these are the shipped defaults.
const SERVICE_CARDS = [
  { icon: Calendar, key: "one", to: "/appointments" },
  { icon: ShoppingBag, key: "two", to: "/shop" },
  { icon: Flower2, key: "three", to: "/garbhsanskar" },
  { icon: MessageCircle, key: "four", to: "/chat" },
];

const doshas = [
  { name: "Vata", color: "from-blue-50 to-amber-50", traits: "Air & Ether — creative, light, swift." },
  { name: "Pitta", color: "from-orange-50 to-rose-50", traits: "Fire & Water — sharp, focused, warm." },
  { name: "Kapha", color: "from-emerald-50 to-lime-50", traits: "Earth & Water — calm, grounded, steady." },
];

export default function Home() {
  const { c, img } = useContent();
  return (
    <main>
      <EntryPopup />

      {/* FEATURED PRODUCTS */}
      <FeaturedProducts />

      {/* PROGRAMME CARDS */}
      <ProgramCards />

      {/* SERVICES */}
      <section className="border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-28">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="overline mb-3">{c("home.practice.overline")}</div>
              <h2 className="font-display text-4xl lg:text-5xl max-w-xl" data-testid="home-practice-title">{c("home.practice.title")}</h2>
            </div>
            <p className="max-w-sm text-muted-foreground">{c("home.practice.subtitle")}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICE_CARDS.map((card, i) => (
              <Link key={card.key} to={card.to} data-testid={`service-card-${i}`}
                className="group border border-border rounded-2xl p-6 hover:border-primary/40 hover:bg-accent/40 transition-all">
                <card.icon className="text-secondary mb-5" size={28} />
                <h3 className="font-display text-2xl mb-2">{c(`home.services.${card.key}_title`)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c(`home.services.${card.key}_desc`)}</p>
                <div className="mt-4 text-sm text-primary flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Discover <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden border-t border-border">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center px-6 lg:px-12 py-16 lg:py-24">
          <div className="lg:col-span-6 fade-in">
            <div className="overline mb-4" data-testid="home-hero-overline">{c("home.hero.overline")}</div>
            <h1 className="font-display text-5xl lg:text-7xl leading-[1.05] tracking-tight text-foreground" data-testid="home-hero-title">
              {c("home.hero.title")} <span className="text-secondary italic">{c("home.hero.highlight")}</span> {c("home.hero.title_end")}
            </h1>
            <p className="mt-6 text-base lg:text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="home-hero-subtitle">
              {c("home.hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/appointments">
                <Button className="btn-action px-7 h-12" data-testid="hero-book-btn">
                  <Calendar size={16} className="mr-2" /> {c("home.hero.primary_cta")}
                </Button>
              </Link>
              <Link to="/shop">
                <Button variant="outline" className="btn-pill border-primary/30 text-primary hover:bg-accent h-12 px-7" data-testid="hero-shop-btn">
                  {c("home.hero.secondary_cta")} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/upload-prescription">
                <Button variant="outline" className="btn-pill border-secondary/40 text-secondary hover:bg-secondary/10 h-12 px-7" data-testid="hero-rx-btn">
                  <FileUp size={16} className="mr-2" /> Upload Prescription
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-8 mt-10 text-sm text-muted-foreground">
              <div><span className="font-display text-3xl text-primary">{c("home.stats.one_value")}</span><div>{c("home.stats.one_label")}</div></div>
              <div className="w-px h-10 bg-border" />
              <div><span className="font-display text-3xl text-primary">{c("home.stats.two_value")}</span><div>{c("home.stats.two_label")}</div></div>
              <div className="w-px h-10 bg-border" />
              <div><span className="font-display text-3xl text-primary">{c("home.stats.three_value")}</span><div>{c("home.stats.three_label")}</div></div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full bg-secondary/10 blur-3xl" />
            <div className="relative rounded-[2rem] overflow-hidden border border-border shadow-sm aspect-[4/5]">
              <img src={img("home.hero.image")} alt={c("home.hero.badge_title")} className="w-full h-full object-cover" data-testid="home-hero-image" />
              <div className="absolute bottom-6 left-6 right-6 bg-background/85 backdrop-blur-sm rounded-xl p-4 border border-border">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><Leaf size={16}/></span>
                  <div>
                    <div className="font-medium text-sm">{c("home.hero.badge_title")}</div>
                    <div className="text-xs text-muted-foreground">{c("home.hero.badge_note")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DOSHA */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-28 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <img src={img("home.dosha.image")} alt={c("home.dosha.title")} className="rounded-[2rem] w-full aspect-[5/4] object-cover border border-border" data-testid="home-dosha-image" />
          </div>
          <div>
            <div className="overline mb-3">{c("home.dosha.overline")}</div>
            <h2 className="font-display text-4xl lg:text-5xl mb-4">{c("home.dosha.title")}</h2>
            <p className="text-muted-foreground mb-8 max-w-md">{c("home.dosha.body")}</p>
            <div className="space-y-3">
              {doshas.map((d) => (
                <div key={d.name} className={`rounded-xl p-5 bg-gradient-to-r ${d.color} border border-border flex items-center justify-between`}>
                  <div>
                    <div className="font-display text-2xl">{d.name}</div>
                    <div className="text-sm text-muted-foreground">{d.traits}</div>
                  </div>
                  <Sparkles className="text-secondary/70" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DOCTOR / CTA */}
      <section className="border-t border-border bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-28 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <div className="overline mb-3" style={{ color: "hsl(20 57% 75%)" }}>{c("home.vaidya.overline")}</div>
            <h2 className="font-display text-4xl lg:text-5xl mb-5 italic">{c("home.vaidya.title")}</h2>
            <p className="opacity-80 max-w-md leading-relaxed">{c("home.vaidya.body")}</p>
            <Link to="/appointments">
              <Button className="btn-action mt-8 h-12 px-7" data-testid="cta-book-btn">
                {c("home.vaidya.cta")}
              </Button>
            </Link>
          </div>
          <img src={img("home.vaidya.image")} alt={c("home.vaidya.title")} className="rounded-[2rem] aspect-[4/3] object-cover" data-testid="home-vaidya-image" />
        </div>
      </section>

    </main>
  );
}
